import type { NotificationBucket, NotificationCenterItem, NotificationCenterTab } from './types';

function startOfDay(ms: number): number {
	const d = new Date(ms);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}

function formatDayLabel(ms: number, now = new Date()): string {
	const date = new Date(ms);
	const sameYear = date.getFullYear() === now.getFullYear();
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		...(sameYear ? {} : { year: 'numeric' }),
	});
}

export function filterByTab(
	items: NotificationCenterItem[],
	tab: NotificationCenterTab
): NotificationCenterItem[] {
	if (tab === 'all') {
		return items;
	}
	return items.filter((item) => item.category === tab);
}

export function bucketNotifications(
	items: NotificationCenterItem[],
	now = new Date()
): NotificationBucket[] {
	const spotlight = items.filter((item) => item.isPinned);
	const unpinned = items.filter((item) => !item.isPinned);

	const buckets: NotificationBucket[] = [];

	if (spotlight.length > 0) {
		buckets.push({ type: 'spotlight', items: spotlight });
	}

	const byDay = new Map<number, NotificationCenterItem[]>();
	for (const item of unpinned) {
		const key = startOfDay(item.createdAt);
		const existing = byDay.get(key) ?? [];
		existing.push(item);
		byDay.set(key, existing);
	}

	const dayKeys = Array.from(byDay.keys()).sort((a, b) => b - a);
	for (const dayKey of dayKeys) {
		const dayItems = byDay.get(dayKey) ?? [];
		buckets.push({
			type: 'day',
			dayKey: String(dayKey),
			label: formatDayLabel(dayKey, now),
			items: dayItems,
		});
	}

	return buckets;
}

export function formatCardTime(ms: number): string {
	if (!ms) {
		return '';
	}
	return new Date(ms).toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
	});
}

export function formatSpotlightDay(ms: number, now = new Date()): string {
	if (!ms) {
		return '';
	}
	return formatDayLabel(ms, now);
}

export function isInternalAppPath(url: string): boolean {
	if (!url) {
		return false;
	}
	// Reject protocol-relative and backslash tricks (`/\evil.com` → off-origin).
	if (url.startsWith('//') || url.includes('\\')) {
		return false;
	}
	if (url.startsWith('/')) {
		return true;
	}
	try {
		const parsed = new URL(url, window.location.origin);
		return (
			parsed.origin === window.location.origin &&
			!parsed.pathname.startsWith('//') &&
			!parsed.pathname.includes('\\')
		);
	} catch {
		return false;
	}
}

export function toInternalPath(url: string): string {
	if (url.startsWith('/') && !url.startsWith('//')) {
		return url;
	}
	try {
		const parsed = new URL(url, window.location.origin);
		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch {
		return url;
	}
}

/** Braze CTAs may be attacker-controlled; only allow http(s) for window.open. */
export function isSafeExternalUrl(url: string): boolean {
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'http:' || parsed.protocol === 'https:';
	} catch {
		return false;
	}
}
